import { getDynamicSemanticToken } from '../../../DesignSystem';
import { getDefaultZoomUI } from '../../../ThemeProvider';
export function getDynamicThemeToken(theme) {
    // Get dynamic semantic design token,
    // which can automatically switch between light and dark content
    const semantic = getDynamicSemanticToken(theme);
    const { typography } = theme?.zoomUI ?? getDefaultZoomUI();
    const isDark = theme?.palette?.mode === 'dark';
    // TODO Temporarily use mixed colors to deal with transparent background problems
    const secondaryBackgroundHover = isDark ? '#3e4247' : '#edeeef';
    const secondaryBackgroundActive = isDark ? '#171719' : '#c2c6ca';
    const dangerSecondaryBackgroundHover = isDark ? '#672331' : '#fce3e7';
    const dangerSecondaryBackgroundActive = isDark ? '#171719' : '#f59eae';
    return {
        fontFamily: typography.fontFamily,
        focusBorderColor: semantic.border.primary,
        focusBorderBackground: semantic.bg.default,
        lineHeight: {
            default: typography.lineHeight.body,
            extraSmall: typography.lineHeight.subtitle,
            small: typography.lineHeight.subtitle,
            medium: typography.lineHeight.body,
            large: typography.lineHeight.bodyLargeMedium,
            extraLarge: typography.lineHeight.bodyLargeMedium,
        },
        minWidth: {
            default: '60px',
            small: '47px',
            medium: '60px',
            large: '73px',
            extraLarge: '81px',
        },
        height: {
            default: '32px',
            small: '24px',
            medium: '32px',
            large: '40px',
            extraLarge: '48px',
        },
        padding: {
            default: '6px 12px',
            small: '4px 8px',
            medium: '6px 12px',
            large: '10px 16px',
            extraLarge: '14px 20px',
        },
        borderRadius: {
            default: '8px',
            small: '6px',
            medium: '8px',
            large: '10px',
            extraLarge: '12px',
        },
        fontSize: {
            default: typography.fontSize.body,
            small: typography.fontSize.body,
            medium: typography.fontSize.body,
            large: typography.fontSize.bodyLargeMedium,
            extraLarge: typography.fontSize.bodyLargeMedium,
        },
        fontWeight: {
            default: typography.fontWeight.body,
            small: typography.fontWeight.body,
            medium: typography.fontWeight.body,
            large: typography.fontWeight.bodyLargeMedium,
            extraLarge: typography.fontWeight.bodyLargeMedium,
        },
        loadingMask: semantic.fill.blend.strong.transparent,
        loadingColor: semantic.fill.contrary.strong.transparent,
        loadingMaskOffset: {
            default: -1,
            primary: -1,
            secondary: 0,
            tertiary: -1,
        },
        color: {
            default: {
                default: semantic.inverse.global.default,
                hover: semantic.inverse.global.default,
                active: semantic.inverse.global.default,
            },
            primary: {
                default: semantic.inverse.global.default,
                hover: semantic.inverse.global.default,
                active: semantic.inverse.global.default,
            },
            secondary: {
                default: semantic.text.stronger.neutral,
                hover: semantic.text.stronger.neutral,
                active: semantic.text.stronger.neutral,
            },
            tertiary: {
                default: semantic.text.primary,
                hover: semantic.text.primary,
                active: semantic.text.primary,
            },
            disabled: {
                default: {
                    default: semantic.state.disable,
                    hover: semantic.state.disable,
                    active: semantic.state.disable,
                },
                primary: {
                    default: semantic.state.disable,
                    hover: semantic.state.disable,
                    active: semantic.state.disable,
                },
                secondary: {
                    default: semantic.state.disable,
                    hover: semantic.state.disable,
                    active: semantic.state.disable,
                },
                tertiary: {
                    default: semantic.state.disable,
                    hover: semantic.state.disable,
                    active: semantic.state.disable,
                },
            },
            danger: {
                default: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                primary: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                secondary: {
                    default: semantic.text.error,
                    hover: semantic.text.error,
                    active: semantic.text.error,
                },
                tertiary: {
                    default: semantic.text.error,
                    hover: semantic.text.error,
                    active: semantic.text.error,
                },
            },
        },
        background: {
            default: {
                default: semantic.fill.global.primary,
                hover: semantic.state.primary.hover,
                active: semantic.state.primary.press,
            },
            primary: {
                default: semantic.fill.global.primary,
                hover: semantic.state.primary.hover,
                active: semantic.state.primary.press,
            },
            secondary: {
                default: semantic.fill.default,
                // hover: semantic.state.subtle.neutral.hover,
                // active: semantic.state.subtle.neutral.press,
                hover: secondaryBackgroundHover,
                active: secondaryBackgroundActive,
            },
            tertiary: {
                default: 'transparent',
                hover: semantic.state.subtle.primary.hover,
                active: semantic.state.subtle.primary.press,
            },
            disabled: {
                default: {
                    default: semantic.state.subtle.disable,
                    hover: semantic.state.subtle.disable,
                    active: semantic.state.subtle.disable,
                },
                primary: {
                    default: semantic.state.subtle.disable,
                    hover: semantic.state.subtle.disable,
                    active: semantic.state.subtle.disable,
                },
                secondary: {
                    default: semantic.fill.default,
                    hover: semantic.fill.default,
                    active: semantic.fill.default,
                },
                tertiary: {
                    default: 'transparent',
                    hover: 'transparent',
                    active: 'transparent',
                },
            },
            danger: {
                default: {
                    default: semantic.fill.global.error,
                    hover: semantic.state.error.hover,
                    active: semantic.state.error.press,
                },
                primary: {
                    default: semantic.fill.global.error,
                    hover: semantic.state.error.hover,
                    active: semantic.state.error.press,
                },
                secondary: {
                    default: semantic.fill.default,
                    // hover: semantic.state.subtle.error.hover,
                    // active: semantic.state.subtle.error.press,
                    hover: dangerSecondaryBackgroundHover,
                    active: dangerSecondaryBackgroundActive,
                },
                tertiary: {
                    default: 'transparent',
                    hover: semantic.state.subtle.error.hover,
                    active: semantic.state.subtle.error.press,
                },
            },
        },
        borderColor: {
            default: {
                default: semantic.fill.global.primary,
                hover: semantic.state.primary.hover,
                active: semantic.state.primary.press,
            },
            primary: {
                default: semantic.fill.global.primary,
                hover: semantic.state.primary.hover,
                active: semantic.state.primary.press,
            },
            secondary: {
                default: semantic.border.neutral,
                hover: semantic.border.neutral,
                active: semantic.border.neutral,
            },
            tertiary: {
                default: 'transparent',
                hover: 'transparent',
                active: 'transparent',
            },
            disabled: {
                default: {
                    default: semantic.state.subtle.disable,
                    hover: semantic.state.subtle.disable,
                    active: semantic.state.subtle.disable,
                },
                primary: {
                    default: semantic.state.subtle.disable,
                    hover: semantic.state.subtle.disable,
                    active: semantic.state.subtle.disable,
                },
                secondary: {
                    default: semantic.state.subtle.disable,
                    hover: semantic.state.subtle.disable,
                    active: semantic.state.subtle.disable,
                },
                tertiary: {
                    default: 'transparent',
                    hover: 'transparent',
                    active: 'transparent',
                },
            },
            danger: {
                default: {
                    default: semantic.fill.global.error,
                    hover: semantic.state.error.hover,
                    active: semantic.state.error.press,
                },
                primary: {
                    default: semantic.fill.global.error,
                    hover: semantic.state.error.hover,
                    active: semantic.state.error.press,
                },
                secondary: {
                    default: semantic.border.error,
                    hover: semantic.border.error,
                    active: semantic.border.error,
                },
                tertiary: {
                    default: 'transparent',
                    hover: 'transparent',
                    active: 'transparent',
                },
            },
        },
        iconButton: {
            width: {
                default: '32px',
                extraSmall: '20px',
                small: '24px',
                medium: '32px',
                large: '40px',
                extraLarge: '48px',
            },
            height: {
                default: '32px',
                extraSmall: '20px',
                small: '24px',
                medium: '32px',
                large: '40px',
                extraLarge: '48px',
            },
            fontSize: {
                default: '16px',
                extraSmall: '14px',
                small: '14px',
                medium: '16px',
                large: '18px',
                extraLarge: '20px',
            },
            iconSize: {
                default: '14px',
                extraSmall: '14px',
                small: '14px',
                medium: '14px',
                large: '16px',
                extraLarge: '18px',
            },
            borderRadius: {
                default: '8px',
                extraSmall: '6px',
                small: '6px',
                medium: '8px',
                large: '10px',
                extraLarge: '12px',
            },
            color: {
                default: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                primary: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                secondary: {
                    default: semantic.text.stronger.neutral,
                    hover: semantic.text.stronger.neutral,
                    active: semantic.text.stronger.neutral,
                },
                tertiary: {
                    default: semantic.text.stronger.neutral,
                    hover: semantic.text.stronger.neutral,
                    active: semantic.text.stronger.neutral,
                },
                overlay: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                disabled: {
                    default: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                    primary: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                    secondary: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                    tertiary: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                    overlay: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                },
                loading: {
                    default: {
                        default: semantic.fill.global.strong.light.transparent,
                        hover: semantic.fill.global.strong.light.transparent,
                        active: semantic.fill.global.strong.light.transparent,
                    },
                    primary: {
                        default: semantic.fill.global.strong.light.transparent,
                        hover: semantic.fill.global.strong.light.transparent,
                        active: semantic.fill.global.strong.light.transparent,
                    },
                    secondary: {
                        default: semantic.fill.contrary.strong.transparent,
                        hover: semantic.fill.contrary.strong.transparent,
                        active: semantic.fill.contrary.strong.transparent,
                    },
                    tertiary: {
                        default: semantic.fill.contrary.strong.transparent,
                        hover: semantic.fill.contrary.strong.transparent,
                        active: semantic.fill.contrary.strong.transparent,
                    },
                    overlay: {
                        default: semantic.inverse.global.default,
                        hover: semantic.inverse.global.default,
                        active: semantic.inverse.global.default,
                    },
                },
            },
            background: {
                default: {
                    default: semantic.fill.global.primary,
                    hover: semantic.state.primary.hover,
                    active: semantic.state.primary.press,
                },
                primary: {
                    default: semantic.fill.global.primary,
                    hover: semantic.state.primary.hover,
                    active: semantic.state.primary.press,
                },
                secondary: {
                    default: semantic.fill.default,
                    // hover: semantic.state.subtle.neutral.hover,
                    // active: semantic.state.subtle.neutral.press,
                    hover: secondaryBackgroundHover,
                    active: secondaryBackgroundActive,
                },
                tertiary: {
                    default: 'transparent',
                    hover: semantic.state.subtle.neutral.hover,
                    active: semantic.state.subtle.neutral.press,
                },
                overlay: {
                    default: semantic.fill.global.dark.transparent,
                    hover: semantic.state.global.dark.transparent.hover,
                    active: semantic.state.global.dark.transparent.press,
                },
                disabled: {
                    default: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    primary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    secondary: {
                        default: semantic.fill.default,
                        hover: semantic.fill.default,
                        active: semantic.fill.default,
                    },
                    tertiary: {
                        default: 'transparent',
                        hover: 'transparent',
                        active: 'transparent',
                    },
                    overlay: {
                        default: semantic.fill.global.dark.transparent,
                        hover: semantic.fill.global.dark.transparent,
                        active: semantic.fill.global.dark.transparent,
                    },
                },
            },
            borderColor: {
                default: {
                    default: semantic.fill.global.primary,
                    hover: semantic.state.primary.hover,
                    active: semantic.state.primary.press,
                },
                primary: {
                    default: semantic.fill.global.primary,
                    hover: semantic.state.primary.hover,
                    active: semantic.state.primary.press,
                },
                secondary: {
                    default: semantic.border.neutral,
                    hover: semantic.border.neutral,
                    active: semantic.border.neutral,
                },
                tertiary: {
                    default: 'transparent',
                    hover: 'transparent',
                    active: 'transparent',
                },
                overlay: {
                    default: 'transparent',
                    hover: 'transparent',
                    active: 'transparent',
                },
                disabled: {
                    default: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    primary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    secondary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    tertiary: {
                        default: 'transparent',
                        hover: 'transparent',
                        active: 'transparent',
                    },
                    overlay: {
                        default: 'transparent',
                        hover: 'transparent',
                        active: 'transparent',
                    },
                },
            },
        },
        homeButton: {
            width: '56px',
            height: '56px',
            fontSize: '30px',
            borderRadius: '16px',
            color: {
                default: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                primary: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                secondary: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                tertiary: {
                    default: semantic.inverse.global.default,
                    hover: semantic.inverse.global.default,
                    active: semantic.inverse.global.default,
                },
                disabled: {
                    default: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                    primary: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                    secondary: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                    tertiary: {
                        default: semantic.state.disable,
                        hover: semantic.state.disable,
                        active: semantic.state.disable,
                    },
                },
            },
            background: {
                default: {
                    default: semantic.fill.complementary,
                    hover: semantic.state.complementary.hover,
                    active: semantic.state.complementary.press,
                },
                primary: {
                    default: semantic.fill.complementary,
                    hover: semantic.state.complementary.hover,
                    active: semantic.state.complementary.press,
                },
                secondary: {
                    default: semantic.fill.global.primary,
                    hover: semantic.state.primary.hover,
                    active: semantic.state.primary.press,
                },
                tertiary: {
                    default: semantic.fill.global.error,
                    hover: semantic.state.error.hover,
                    active: semantic.state.error.press,
                },
                disabled: {
                    default: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    primary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    secondary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    tertiary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                },
            },
            borderColor: {
                default: {
                    default: semantic.fill.complementary,
                    hover: semantic.state.complementary.hover,
                    active: semantic.state.complementary.press,
                },
                primary: {
                    default: semantic.fill.complementary,
                    hover: semantic.state.complementary.hover,
                    active: semantic.state.complementary.press,
                },
                secondary: {
                    default: semantic.fill.global.primary,
                    hover: semantic.state.primary.hover,
                    active: semantic.state.primary.press,
                },
                tertiary: {
                    default: semantic.fill.global.error,
                    hover: semantic.state.error.hover,
                    active: semantic.state.error.press,
                },
                disabled: {
                    default: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    primary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    secondary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                    tertiary: {
                        default: semantic.state.subtle.disable,
                        hover: semantic.state.subtle.disable,
                        active: semantic.state.subtle.disable,
                    },
                },
            },
        },
    };
}
